import React, { useEffect, useMemo, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

const DEFAULT_FIELDS = {
  categoryKey: "category",
  minKey: "min",
  q1Key: "q1",
  medianKey: "median",
  q3Key: "q3",
  maxKey: "max",
};

export default function AgentBoxWhiskerChart({ rows = [], config = {} }) {
  const chartRef = useRef(null);
  const fields = { ...DEFAULT_FIELDS, ...(config || {}) };
  const data = useMemo(() => rows.map((row) => ({
    ...row,
    [fields.categoryKey]: String(row?.[fields.categoryKey] ?? ""),
    [fields.minKey]: Number(row?.[fields.minKey]),
    [fields.q1Key]: Number(row?.[fields.q1Key]),
    [fields.medianKey]: Number(row?.[fields.medianKey]),
    [fields.q3Key]: Number(row?.[fields.q3Key]),
    [fields.maxKey]: Number(row?.[fields.maxKey]),
  })).filter((row) => {
    const values = [fields.minKey, fields.q1Key, fields.medianKey, fields.q3Key, fields.maxKey]
      .map((key) => row[key]);
    return row[fields.categoryKey] && values.every(Number.isFinite) && values.every((value, index) => !index || value >= values[index - 1]);
  }), [rows, fields.categoryKey, fields.minKey, fields.q1Key, fields.medianKey, fields.q3Key, fields.maxKey]);

  useEffect(() => {
    if (!chartRef.current || !data.length) return undefined;
    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chart.data = data;
    chart.padding(18, 24, 8, 8);

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = fields.categoryKey;
    categoryAxis.renderer.grid.template.disabled = true;
    categoryAxis.renderer.labels.template.fill = am4core.color("#cbd5e1");
    categoryAxis.renderer.minGridDistance = 35;
    categoryAxis.title.text = config.categoryLabel || "";
    categoryAxis.title.fill = am4core.color("#94a3b8");

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.renderer.labels.template.fill = am4core.color("#cbd5e1");
    valueAxis.renderer.grid.template.stroke = am4core.color("#334155");
    valueAxis.title.text = [config.valueLabel, config.valueUnit && `(${config.valueUnit})`].filter(Boolean).join(" ");
    valueAxis.title.fill = am4core.color("#94a3b8");

    const boxes = chart.series.push(new am4charts.CandlestickSeries());
    boxes.dataFields.categoryX = fields.categoryKey;
    boxes.dataFields.lowValueY = fields.minKey;
    boxes.dataFields.openValueY = fields.q1Key;
    boxes.dataFields.valueY = fields.q3Key;
    boxes.dataFields.highValueY = fields.maxKey;
    boxes.columns.template.width = am4core.percent(48);
    boxes.columns.template.fill = am4core.color("#0891b2");
    boxes.columns.template.stroke = am4core.color("#67e8f9");
    boxes.columns.template.fillOpacity = 0.45;
    boxes.columns.template.strokeWidth = 2;
    boxes.tooltipText = `{${fields.categoryKey}}\nMin: {${fields.minKey}}\nQ1: {${fields.q1Key}}\nMedian: {${fields.medianKey}}\nQ3: {${fields.q3Key}}\nMax: {${fields.maxKey}}`;

    const medians = chart.series.push(new am4charts.LineSeries());
    medians.dataFields.categoryX = fields.categoryKey;
    medians.dataFields.valueY = fields.medianKey;
    medians.strokeOpacity = 0;
    const medianBullet = medians.bullets.push(new am4charts.Bullet());
    const medianLine = medianBullet.createChild(am4core.Rectangle);
    medianLine.width = 30;
    medianLine.height = 3;
    medianLine.horizontalCenter = "middle";
    medianLine.verticalCenter = "middle";
    medianLine.fill = am4core.color("#f8fafc");

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "none";
    return () => chart.dispose();
  }, [data, config.categoryLabel, config.valueLabel, config.valueUnit, fields.categoryKey, fields.minKey, fields.q1Key, fields.medianKey, fields.q3Key, fields.maxKey]);

  if (!data.length) return <div className="agent-resultsError">No valid box-and-whisker rows were provided.</div>;
  return <div className="agent-boxWhiskerChart" ref={chartRef} />;
}
